import { ReactNode } from 'react';
import { Navigate, useLocation, type Location } from 'react-router-dom';

import { useAuth } from '@/commons/useAuth';
import { PATH } from '@/commons/constants';
import { getRedirectPathAfterLogin } from '../utils/redirectAfterLogin';
import { getURLParam } from '../utils/url';

/** State stored on location when redirecting from an auth-required page to login */
interface LoginRedirectState {
  from?: Location;
}

/** GuestGuard props */
interface GuestGuardProps {
  /** Content to render when not authenticated (e.g. login form) */
  children: ReactNode;
}

/**
 * Guard component that redirects authenticated users to dashboard
 * and allows unauthenticated users to access the login page.
 * Exception: If user is on signup page at step 4, allow them to stay
 * so they can see the completion screen after auth is set.
 */
export const GuestGuard: React.FC<GuestGuardProps> = ({ children }) => {
  const { auth } = useAuth();
  const location = useLocation();

  if (auth) {
    const isSignupStep4 =
      location.pathname === PATH.SIGNUP && getURLParam('step') === '4';
    if (isSignupStep4) {
      return <>{children}</>;
    }

    const isResetPassword =
      location.pathname === PATH.RESET_PASSWORD ||
      location.pathname === PATH.SEND_RESET_MAIL;
    if (isResetPassword) {
      return <>{children}</>;
    }

    const from = (location.state as LoginRedirectState)?.from;
    const redirectPath = getRedirectPathAfterLogin(from, PATH.DASHBOARD);
    return <Navigate to={redirectPath} replace state={{}} />;
  }

  return <>{children}</>;
};
